import {
  type ChangeEvent,
  type FocusEvent,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import FormError from '@/components/FormError';
import useCountryWithCertainty from '@/hooks/useCountryWithCertainty';
import useMediaQuery from '@/hooks/useMediaQuery';
import CountrySelect from './CountrySelect';
import detectIsoFromPrefix from './detectIsoFromPrefix.util';
import resolveToE164 from './resolveToE164.util';
import useLocalizedCountries from './useLocalizedCountries';
import type { FormPhoneInputProps } from './types';
import styles from './FormPhoneInput.module.scss';

/**
 * Use the bottom-sheet country picker on touch-primary devices OR on any
 * viewport narrower than the tablet breakpoint. The comma in the media query
 * is "or" — so simply resizing a desktop browser flips the picker UI too.
 */
const TOUCH_QUERY = '(pointer: coarse), (max-width: 1023px)';

/** Phone input only allows digits, whitespace, and `+ - ( )`. */
const ALLOWED_CHARS = /[^\d\s+\-()]/g;

const FormPhoneInput = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  errorMessage,
  condensed = false,
  disabled = false,
  optional = false,
  optionalLabel,
  required = false,
  autoComplete = 'tel',
  placeholder,
  className,
  ref,
}: FormPhoneInputProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!);

  const isTouch = useMediaQuery(TOUCH_QUERY);
  const region = useCountryWithCertainty();
  const countries = useLocalizedCountries();

  // Initial country: prefer the one inferred from a pre-filled value (e.g.
  // editing an existing contact), then fall back to the user's region, then AT.
  const isoFromValue = value ? detectIsoFromPrefix(value) : null;
  const regionIso =
    region.country && countries.some((c) => c.iso === region.country)
      ? region.country
      : null;
  const defaultIso = isoFromValue ?? regionIso ?? 'AT';

  const [selectedIso, setSelectedIso] = useState<string>(defaultIso);
  const selectedCountry = countries.find((c) => c.iso === selectedIso);

  // Strip the country's dial prefix from the value shown in the input — the
  // country is already represented by the flag/prefix in the picker. The
  // outer `value` is preserved as-is for callers; `trimStart` handles the
  // case where a pasted "+43 676 …" leaves a leading space behind.
  const displayedValue =
    selectedCountry && value.startsWith(selectedCountry.prefix)
      ? value.slice(selectedCountry.prefix.length).trimStart()
      : value;

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const cleaned = raw.replace(ALLOWED_CHARS, '');

    // Only treat the typed value as international when it actually starts
    // with a "+" or "00" — otherwise plain local digits like "676…" would
    // accidentally hijack the country dropdown.
    const looksInternational =
      cleaned.startsWith('+') || cleaned.startsWith('00');

    let nextValue = cleaned;
    let strippedChars = 0;
    // Track the effective country separately: when a new country is detected
    // from an international prefix, selectedCountry is still stale (setState
    // is async), so we need the freshly detected one to build the full value.
    let effectiveCountry = selectedCountry;

    if (looksInternational) {
      const detected = detectIsoFromPrefix(cleaned);
      const detectedCountry = detected
        ? countries.find((c) => c.iso === detected)
        : undefined;

      if (detected && detectedCountry) {
        if (detected !== selectedIso) {
          setSelectedIso(detected);
        }
        effectiveCountry = detectedCountry;
        // Hand the prefix off to the country dropdown: strip the matched
        // "+XX" or "00XX" from the input so the user keeps typing local
        // digits. Without this, finishing the prefix would leave it sitting
        // in the field and the next render would visibly snap it away.
        const plusForm = detectedCountry.prefix;
        const zeroForm = `00${plusForm.slice(1)}`;
        if (cleaned.startsWith(plusForm)) {
          nextValue = cleaned.slice(plusForm.length).trimStart();
          strippedChars = cleaned.length - nextValue.length;
        } else if (cleaned.startsWith(zeroForm)) {
          nextValue = cleaned.slice(zeroForm.length).trimStart();
          strippedChars = cleaned.length - nextValue.length;
        }
      }
    }

    // Always emit the full value (prefix + local digits) so callers and
    // validation hooks receive a dialable number, not bare local digits.
    const fullValue = `${effectiveCountry?.prefix ?? ''}${nextValue}`;

    // Fast path: nothing was stripped — let the browser keep the caret where
    // it is (Firefox loses caret position if we re-set the same value).
    if (nextValue === raw) {
      onChange?.(fullValue);
      return;
    }

    // Characters were stripped: restore the caret manually before React
    // commits the cleaned value (otherwise Firefox jumps to the end).
    const input = event.target;
    const selectionEnd = input.selectionEnd ?? raw.length;
    const rawCursor = raw
      .slice(0, selectionEnd)
      .replace(ALLOWED_CHARS, '').length;
    const cursor = Math.max(0, rawCursor - strippedChars);
    requestAnimationFrame(() => input.setSelectionRange(cursor, cursor));

    onChange?.(fullValue);
  };

  const handleCountryChange = (iso: string) => {
    const newCountry = countries.find((c) => c.iso === iso);
    setSelectedIso(iso);

    if (newCountry) {
      // Swap the dial prefix while preserving any local digits the user has
      // already typed:
      //   "+43 676 …" + DE → "+49676…"
      //   "0676…"     + DE → "+49676…"  (local trunk gets normalised first)
      //   ""          + DE → "+49"       (empty / prefix-only seeds the new prefix)
      const e164 = resolveToE164(value, selectedCountry);
      const localPart =
        selectedCountry && e164.startsWith(selectedCountry.prefix)
          ? e164.slice(selectedCountry.prefix.length)
          : '';
      onChange?.(`${newCountry.prefix}${localPart}`);
    }

    // Defer focus until the modal <dialog> has unmounted — focusing an
    // element outside an open modal dialog is a no-op (the browser traps
    // focus inside).
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) {
        return;
      }
      input.focus();
      const end = input.value.length;
      input.setSelectionRange(end, end);
    });
  };

  const handleBlur: FormPhoneInputProps['onBlur'] = (event) => {
    if (!onBlur) {
      return;
    }
    // Resolve to E.164 so RHF and validation receive the canonical form
    // (e.g. "0676 123456" + AT → "+43676123456").
    const resolved = resolveToE164(event.target.value, selectedCountry);
    onChange?.(resolved);
    const syntheticEvent = {
      ...event,
      target: { ...event.target, value: resolved },
    } as FocusEvent<HTMLInputElement>;
    onBlur(syntheticEvent);
  };

  return (
    <div className={classNames(className)}>
      <label
        htmlFor={inputId}
        className={classNames(styles.label, { [styles.condensed]: condensed })}
      >
        {label}
        {optional && (
          <span className={styles.optionalTag}>
            {optionalLabel ?? 'Optional'}
          </span>
        )}
      </label>
      <div
        className={classNames(styles.inputWrapper, {
          [styles.hasError]: !!errorMessage,
          [styles.disabled]: disabled,
          [styles.condensed]: condensed,
        })}
      >
        <CountrySelect
          countries={countries}
          value={selectedIso}
          onChange={handleCountryChange}
          isTouch={isTouch}
        />
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="tel"
          autoComplete={autoComplete}
          className={styles.input}
          value={displayedValue}
          onChange={handlePhoneChange}
          onBlur={handleBlur}
          placeholder={placeholder ?? ''}
          required={required}
          disabled={disabled}
          aria-label={label}
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? `${inputId}-error` : undefined}
        />
      </div>
      {errorMessage && !condensed && <FormError message={errorMessage} />}
    </div>
  );
};

export default FormPhoneInput;
